import Component from '../shared/component'

export const Markup = Component('v-markup', {
  functional: true,
  props: {
    content: {
      type: String,
      default: null,
      required: true
    }
  },
  render (createElement, {props}) {
    return parse(props.content)
      .map(c => virtualize(c, createElement))
      .filter(c => typeof c !== 'string' || c.trim() !== '')
      .map(c => typeof c === 'string' ? createElement('span', [c]) : c)
  }
})

/**
 * Whitelist of allowed elements, attributes, and protocols.
 */
const WHITELIST = {
  /**
   * Allowed elements; elements not present in this list will have their raw
   * HTML displayed.
   */
  elements: {
    a: true,
    p: true,
    strong: true,
    em: true,
    br: true,
    h1: true,
    h2: true,
    h3: true,
    h4: true,
    h5: true,
    h6: true,
    code: true
  },

  /**
   * Allowed attributes; attributes not present in this list will be removed.
   */
  attributes: {
    a: {
      href: true,
      target: true,
      rel: true
    },
    p: {
      class: true
    }
  },

  /**
   * Allowed protocols; protocols not present in this list will have their
   * associated attribute removed.
   */
  protocols: {
    a: {
      href: {
        http: true,
        https: true,
        mailto: true
      }
    }
  }
}

/**
 * Transformer functions for pre-processing elements.
 */
const TRANSFORMERS = {
  /**
   * Transformer function for anchor nodes.
   *
   * @param {Object} node The node to transform.
   */
  a (node) {
    // Avoid leaking partial access to the linking page if the linked page is
    // opened in a new window.
    // https://mathiasbynens.github.io/rel-noopener/
    if (node.target === '_blank') {
      node.rel += ' noopener noreferrer'
    }
  }
}

/**
 * Parse an HTML string to DOM nodes. This makes use of a <div> tag for
 * performance reasons as this approach by far outperforms e.g. DOMParser.
 *
 * @param {String} html The HTML to parse.
 * @param {Document} [context=document] The context in which to parse the HTML.
 * @return {Array<Node>} The parsed DOM nodes.
 */
function parse (html, context = document) {
  const element = context.createElement('div')
  element.innerHTML = html
  return [...element.childNodes]
}

/**
 * Recursively convert a given node and its decendants to a virtual DOM node.
 *
 * @param {Node} node The node to virtualise.
 * @return {VNode} The resulting virtual DOM node.
 */
function virtualize (node, createElement) {
  // Extract the element, attribute, and protocol whitelists and assign them to
  // shorter aliases. They are referenced a lot below so this makes for much
  // more concise code.
  const {elements: E, attributes: A, protocols: P} = WHITELIST

  switch (node.nodeType) {
    case node.ELEMENT_NODE: {
      const tag = node.nodeName.toLowerCase()

      if (!E[tag]) {
        return node.outerHTML
      }

      (TRANSFORMERS[tag] || (n => n))(node)

      const attrs = {}

      for (let i = 0, n = node.attributes.length; i < n; i++) {
        const {name, value} = node.attributes[i]

        if (A[tag] && A[tag][name]) {
          // Additionally verify that the protocol of the attribute value is
          // valid. This will only be applicable in cases where attributes can
          // reference resources by URL.
          if (P[tag] && P[tag][name]) {
            const url = new URL(value, document.URL)
            const [protocol] = url.protocol.split(':')

            if (!P[tag][name][protocol]) {
              continue
            }
          }

          attrs[name] = value === '' || value === name ? true : value
        }
      }

      const children = []

      for (let i = 0, n = node.childNodes.length; i < n; i++) {
        const child = virtualize(node.childNodes[i], createElement)

        if (child) {
          children.push(child)
        }
      }

      return createElement(tag, {attrs}, children)
    }

    case node.TEXT_NODE:
      return node.nodeValue

    default: return null
  }
}
